import { create } from 'zustand';
import { OrderHistory, adjustProductPriceForQuantityInfo } from './appOrderHistoryStore';
import { CustomIngredient, fetchCustomIngredientsByRestaurant } from '../services/customIngredients/customIngredientsService';
import { Subrecipe, fetchSubrecipesByRestaurant } from '../services/subrecipes/subrecipesService';
import { fetchOrderHistoryByRestaurant } from '../services/orderhistory/orderhistoryService';

/**
 * Cross-restaurant sourcing cache. Holds the cost-price input data
 * (orders + custom ingredients + subrecipes) of SIBLING restaurants the user
 * borrows ingredients from, keyed by restaurantId — WITHOUT touching the
 * single-restaurant stores the cost-price page already uses (`appOrderHistoryStore`
 * etc.), so the current restaurant's loaded data is never clobbered.
 *
 * ⚠️ Not to be confused with `appCrossRestaurantPricesStore` (a price-difference
 * analytics calculator). This store ties 1:1 to an ingredient's `sourceRestaurantId`.
 *
 * Orders are passed through the SAME per-kg/L adjuster the app applies at load time
 * (`adjustProductPriceForQuantityInfo`) so sibling prices share the exact same basis
 * as same-restaurant prices.
 */
export interface SourceRestaurantData {
  orders: OrderHistory[];
  customIngredients: CustomIngredient[];
  subrecipes: Map<string, Subrecipe>;
}

interface SourceRestaurantDataState {
  // Cached data per restaurantId.
  dataByRestaurant: Record<string, SourceRestaurantData>;
  // Per-restaurant loading / error flags for UI feedback.
  loadingByRestaurant: Record<string, boolean>;
  errorByRestaurant: Record<string, boolean>;
  // In-flight promises so concurrent ensureLoaded() calls dedupe.
  ensureLoaded: (restaurantId: string) => Promise<void>;
  clear: () => void;
}

// Module-level in-flight map (kept out of the store so it never triggers renders).
const inFlight = new Map<string, Promise<void>>();
let sourceGeneration = 0;

export const useSourceRestaurantDataStore = create<SourceRestaurantDataState>((set, get) => ({
  dataByRestaurant: {},
  loadingByRestaurant: {},
  errorByRestaurant: {},

  ensureLoaded: async (restaurantId: string) => {
    // Already cached → nothing to do.
    if (get().dataByRestaurant[restaurantId]) return;
    // Already loading → wait for the existing fetch.
    const existing = inFlight.get(restaurantId);
    if (existing) return existing;

    const generation = sourceGeneration;
    const promise = (async () => {
      set(state => ({
        loadingByRestaurant: { ...state.loadingByRestaurant, [restaurantId]: true },
        errorByRestaurant: { ...state.errorByRestaurant, [restaurantId]: false },
      }));

      try {
        const [rawOrders, customIngredients, subrecipeList] = await Promise.all([
          fetchOrderHistoryByRestaurant(restaurantId),
          fetchCustomIngredientsByRestaurant(restaurantId),
          fetchSubrecipesByRestaurant(restaurantId),
        ]);

        if (generation !== sourceGeneration) return;

        // Apply the same per-kg/L price adjustment the order-history store applies
        // at load time, so the pricing basis matches the rest of the app.
        const orders = rawOrders.map(o => ({
          ...o,
          productData: o.productData.map(p => adjustProductPriceForQuantityInfo(p, o.platform)),
        }));

        const subrecipes = new Map<string, Subrecipe>();
        subrecipeList.forEach(s => subrecipes.set(s.id, s));

        set(state => ({
          dataByRestaurant: {
            ...state.dataByRestaurant,
            [restaurantId]: { orders, customIngredients, subrecipes },
          },
          loadingByRestaurant: { ...state.loadingByRestaurant, [restaurantId]: false },
        }));
      } catch (err) {
        if (generation !== sourceGeneration) return;
        console.error('[ERROR] loading source restaurant data', restaurantId, err);
        set(state => ({
          loadingByRestaurant: { ...state.loadingByRestaurant, [restaurantId]: false },
          errorByRestaurant: { ...state.errorByRestaurant, [restaurantId]: true },
        }));
      } finally {
        if (generation === sourceGeneration) inFlight.delete(restaurantId);
      }
    })();

    inFlight.set(restaurantId, promise);
    return promise;
  },

  clear: () => {
    sourceGeneration++;
    inFlight.clear();
    set({ dataByRestaurant: {}, loadingByRestaurant: {}, errorByRestaurant: {} });
  },
}));
